import type { WooOrder, WooPixPaymentDetails } from "../types";

import { isPaidOrder } from "./orderHelpers";

const PIX_GATEWAY_FRAGMENT = "pix";
const PAGARME_RESPONSE_META_KEY = "_pagarme_response_data";
const PAGARME_PAYMENT_METHOD_META_KEY = "_pagarme_payment_method";

type PagarmePostData = {
  qr_code?: string;
  qr_code_url?: string;
  expires_at?: string;
  status?: string;
};

type PagarmeTransaction = {
  status?: string;
  postData?: PagarmePostData;
};

type PagarmeCharge = {
  status?: string;
  transactions?: PagarmeTransaction[];
};

type PagarmeResponsePayload = {
  status?: string;
  charges?: PagarmeCharge[];
};

function getMetaValue(order: WooOrder, key: string): string | undefined {
  return order.metaData?.find((entry) => entry.key === key)?.value;
}

/** Pedido com método PIX Pagarme (gateway ou meta do plugin). */
export function isPixPaymentOrder(order: WooOrder): boolean {
  if (order.paymentMethod?.toLowerCase().includes(PIX_GATEWAY_FRAGMENT)) {
    return true;
  }
  return getMetaValue(order, PAGARME_PAYMENT_METHOD_META_KEY) === PIX_GATEWAY_FRAGMENT;
}

/** Extrai QR/copia-e-cola do meta `_pagarme_response_data`. */
export function extractPixPaymentFromOrder(
  order: WooOrder,
): WooPixPaymentDetails | null {
  const raw = getMetaValue(order, PAGARME_RESPONSE_META_KEY);
  if (!raw) return null;

  try {
    const payload = JSON.parse(raw) as PagarmeResponsePayload;
    const transaction = payload.charges?.[0]?.transactions?.[0];
    const postData = transaction?.postData;

    if (!postData?.qr_code?.trim()) return null;

    return {
      qrCode: postData.qr_code.trim(),
      qrCodeUrl: postData.qr_code_url?.trim() ?? "",
      expiresAt: postData.expires_at ?? null,
      transactionStatus:
        transaction?.status ?? postData.status ?? payload.status ?? "",
      pagarmeStatus: payload.status ?? "",
    };
  } catch {
    return null;
  }
}

function isPixTransactionAwaitingPayment(details: WooPixPaymentDetails): boolean {
  const status = details.transactionStatus.toLowerCase();
  return status === "waiting_payment" || status === "pending";
}

function isPixQrExpired(details: WooPixPaymentDetails): boolean {
  if (!details.expiresAt) return false;
  const expiresAt = new Date(details.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false;
  return expiresAt.getTime() <= Date.now();
}

/** PIX ativo aguardando pagamento — exibe QR inline em vez de reabrir popup. */
export function hasActivePixQrCode(order: WooOrder): boolean {
  if (isPaidOrder(order)) return false;
  if (!isPixPaymentOrder(order)) return false;

  const details = extractPixPaymentFromOrder(order);
  if (!details) return false;
  if (isPixQrExpired(details)) return false;

  const pagarmeStatus = details.pagarmeStatus.toLowerCase();
  if (pagarmeStatus === "paid" || pagarmeStatus === "canceled") return false;

  return isPixTransactionAwaitingPayment(details);
}
